<template>
<div class="chapter">
<div class="inner_page">
<a id="page_xix"></a>
<h2 class="fm-title"><span class="bold">Author’s Note &amp; Glossary</span></h2>
<p class="noindent">The aviation-minded reader will notice that I have referred to German <em class="italic">Messerschmitt</em> fighters by the abbreviation ‘Me’ (not ‘Bf’, which is more technically correct), or simply by their numeric designation, such as ‘109’ or ‘110’. This not only reads better but is authentic: during the Battle of Britain, Keith Lawrence, a New Zealander, flew Spitfires and once said to me, ‘To us they were just “Me’s”, “109s” or “110s”, simple, never “Bf”.’</p>
<p class="indent">Regarding photographs, certain images for this book were supplied by the families involved and enthusiast friends. The remainder are from my own archive, collated over more than thirty years.</p>
<p class="indent">In another attempt to preserve accuracy, I have also used the original German wherever possible regarding terms associated with the <em class="italic">Luftwaffe</em>, such as: -</p>
<table class="table">
<tbody><tr>
<td class="tab1"><em class="italic"><span href="#/xhtml/17_Chapter10#rglas1" id="glas1" title="Link target outside of Sample">Adlerangriff</span></em></td>
<td class="tab2">‘Attack of the Eagles’</td>
</tr>
<tr>
<td class="tab1"><em class="italic"><span href="#/xhtml/17_Chapter10#rglas1a" id="glas1a" title="Link target outside of Sample">Adlertag</span></em></td>
<td class="tab2">‘Eagle Day’</td>
</tr>
<tr>
<td class="tab1"><em class="italic">Eichenlaub</em></td>
<td class="tab2">The Oak Leaves, essentially being a bar to the <em class="italic">Ritterkreuz</em>.</td>
</tr>
<tr>
<td class="tab1"><em class="italic"><span href="#/xhtml/15_Chapter08#rglas2" id="glas2" title="Link target outside of Sample">Erprobungsgruppe</span></em></td>
<td class="tab2">Experimental group, in the case of <em class="italic">Erprobungsgruppe</em> 210, a skilled precision bombing unit.</td>
</tr>
<tr>
<td class="tab1"><em class="italic"><span href="#/xhtml/22_Chapter15#rglas3" id="glas3" title="Link target outside of Sample">Experte</span></em></td>
<td class="tab2">A fighter ‘ace’. Ace status, on both sides, was achieved by destroying five enemy aircraft.</td>
</tr>
<tr>
<td class="tab1"><em class="italic"><a href="#/xhtml/08_Chapter01#rglas4" id="glas4">Freie hunt</a></em></td>
<td class="tab2">A fighter sweep.</td>
</tr>
<tr>
<td class="tab1"><em class="italic">Gefechstand</em></td>
<td class="tab2">Operations headquarters.</td>
</tr>
<tr>
<td class="tab1"><em class="italic"><span href="#/xhtml/22_Chapter15#rglas5" id="glas5" title="Link target outside of Sample">Geschwader</span></em></td>
<td class="tab2">The whole group, usually of three gruppen.</td>
</tr>
<tr>
<td class="tab1"><em class="italic">Geschwaderkommodore</em></td>
<td class="tab2">The group leader.</td>
</tr>
<tr>
<td class="tab1"><em class="italic"><span href="#/xhtml/15_Chapter08#rglas6" id="glas6" title="Link target outside of Sample">Gruppe</span></em></td>
<td class="tab2">A wing, usually of three squadrons.</td>
</tr>
<tr>
<td class="tab1"><em class="italic"><a href="#/xhtml/08_Chapter01#rglas6a" id="glas6a">Gruppenkeil</a></em></td>
<td class="tab2">A wedge formation of bombers, usually made up of vics of three.</td>
</tr>
<tr>
<td class="tab1"><a id="page_xx"></a><em class="italic">Gruppenkommandeur</em></td>
<td class="tab2">The wing commander.</td>
</tr>
<tr>
<td class="tab1"><em class="italic">Jagdbomber (‘Jabo’)</em></td>
<td class="tab2">Fighter-bomber.</td>
</tr>
<tr>
<td class="tab1"><em class="italic"><a href="#/xhtml/08_Chapter01#rglas7" id="glas7">Jagdflieger</a></em></td>
<td class="tab2">Fighter pilot.</td>
</tr>
<tr>
<td class="tab1"><em class="italic"><span href="#/xhtml/15_Chapter08#rglas8" id="glas8" title="Link target outside of Sample">Jagdgeschwader</span></em></td>
<td class="tab2">Fighter group, abbreviated JG.</td>
</tr>
<tr>
<td class="tab1"><em class="italic"><span href="#/xhtml/15_Chapter08#rglas9" id="glas9" title="Link target outside of Sample">Jagdwaffe</span></em></td>
<td class="tab2">The fighter force.</td>
</tr>
<tr>
<td class="tab1"><em class="italic"><span href="#/xhtml/10_Chapter03#rglas10" id="glas10" title="Link target outside of Sample">Jäger</span></em></td>
<td class="tab2">Hunter, in this context a fighter pilot or aircraft.</td>
</tr>
<tr>
<td class="tab1"><em class="italic">Kampfflieger</em></td>
<td class="tab2">Bomber aircrew.</td>
</tr>
<tr>
<td class="tab1"><em class="italic">Kampfgeschwader</em></td>
<td class="tab2">Bomber group, abbreviated KG.</td>
</tr>
<tr>
<td class="tab1"><em class="italic"><span href="#/xhtml/15_Chapter08#rglas11" id="glas11" title="Link target outside of Sample">Kanal</span></em></td>
<td class="tab2">English Channel.</td>
</tr>
<tr>
<td class="tab1"><em class="italic">Katchmarek</em></td>
<td class="tab2">Wingman.</td>
</tr>
<tr>
<td class="tab1"><em class="italic"><span href="#/xhtml/15_Chapter08#rglas12" id="glas12" title="Link target outside of Sample">Lehrgeschwader</span></em></td>
<td class="tab2">Literally a training group, but actually a precision bombing unit, abbreviated LG.</td>
</tr>
<tr>
<td class="tab1"><em class="italic"><a href="#/xhtml/08_Chapter01#rglas13" id="glas13">Luftflotte</a></em></td>
<td class="tab2">Air Fleet.</td>
</tr>
<tr>
<td class="tab1"><em class="italic">Oberkannone</em></td>
<td class="tab2">Literally the ‘Top Gun’, or leading fighter ace. <em class="italic">Oberkommando der</em></td>
</tr>
<tr>
<td class="tab1"><em class="italic"><a href="#/xhtml/08_Chapter01#rglas14" id="glas14">Wehrmacht</a></em> (OKW)</td>
<td class="tab2">The German armed forces high command.</td>
</tr>
<tr>
<td class="tab1"><em class="italic"><span href="#/xhtml/22_Chapter15#rglas15" id="glas15" title="Link target outside of Sample">Ritterkreuz</span></em></td>
<td class="tab2">The Knight’s Cross of the Iron Cross.</td>
</tr>
<tr>
<td class="tab1"><em class="italic"><span href="#/xhtml/15_Chapter08#rglas16" id="glas16" title="Link target outside of Sample">Rotte</span></em></td>
<td class="tab2">A pair of fighters, comprising leader and wingman, into which the <em class="italic">Schwarm</em> broke once battle was joined.</td>
</tr>
<tr>
<td class="tab1"><em class="italic"><span href="#/xhtml/15_Chapter08#rglas17" id="glas17" title="Link target outside of Sample">Rottenführer</span></em></td>
<td class="tab2">Leader of a fighting pair.</td>
</tr>
<tr>
<td class="tab1"><em class="italic"><a href="#/xhtml/08_Chapter01#rglas18" id="glas18">Schwarm</a></em></td>
<td class="tab2">A section of four fighters.</td>
</tr>
<tr>
<td class="tab1"><em class="italic">Schwarmführer</em></td>
<td class="tab2">Section leader.</td>
</tr>
<tr>
<td class="tab1"><em class="italic"><a href="#/xhtml/08_Chapter01#rglas20" id="glas20">Stab</a></em></td>
<td class="tab2">Staff</td>
</tr>
<tr>
<td class="tab1"><em class="italic"><span href="#/xhtml/15_Chapter08#rglas21" id="glas21" title="Link target outside of Sample">Stabschwarm</span></em></td>
<td class="tab2">Staff flight.</td>
</tr>
<tr>
<td class="tab1"><em class="italic"><span href="#/xhtml/22_Chapter15#rglas22" id="glas22" title="Link target outside of Sample">Staffel</span></em></td>
<td class="tab2">A squadron.</td>
</tr>
<tr>
<td class="tab1"><em class="italic"><span href="#/xhtml/09_Chapter02#rglas23" id="glas23" title="Link target outside of Sample">Staffelkapitän</span></em></td>
<td class="tab2">The squadron leader.</td>
</tr>
<tr>
<td class="tab1"><em class="italic"><span href="#/xhtml/10_Chapter03#rglas24" id="glas24" title="Link target outside of Sample">Störflug</span></em></td>
<td class="tab2">Harrassing attacks, usually by lone Ju 88s.</td>
</tr>
<tr>
<td class="tab1"><em class="italic"><span href="#/xhtml/16_Chapter09#rglas25" id="glas25" title="Link target outside of Sample">Stuka</span></em></td>
<td class="tab2">The Ju 87 dive-bomber.</td>
</tr>
<tr>
<td class="tab1"><em class="italic">Sturkampfgeschwader</em></td>
<td class="tab2">Dive-bomber group, abbreviated StG.</td>
</tr>
<tr>
<td class="tab1"><em class="italic"><span href="#/xhtml/09_Chapter02#rglas26" id="glas26" title="Link target outside of Sample">Vermisst</span></em></td>
<td class="tab2">Missing.</td>
</tr>
<tr>
<td class="tab1"><em class="italic"><span href="#/xhtml/18_Chapter11#rglas27" id="glas27" title="Link target outside of Sample">Zerstörer</span></em></td>
<td class="tab2">Literally ‘destroyer’, the term used for the Me 110.</td>
</tr>
<tr>
<td class="tab1"><em class="italic">Zerstörergeschwader</em></td>
<td class="tab2">Destroyer group, abbreviated ZG.</td>
</tr>
</tbody></table>
<p class="noindent">Each <em class="italic">geschwader</em> generally comprised three <em class="italic">gruppen</em>, each of three <em class="italic">staffeln</em>. Each <em class="italic">gruppe</em> is designated by Roman numerals, i.e. III/JG 26 refers to the third <em class="italic">gruppe</em> of Fighter Group (abbreviated ‘JG’) 26. <em class="italic">Staffeln</em> are identified by numbers, so 7/JG 26 is the 7th <em class="italic">staffel</em> and belongs to III/JG 26.</p>
<a id="page_xxi"></a>
<p class="indent">Rank comparisons may also be useful: -</p>
<table class="table">
<tbody><tr>
<td class="tab1"><em class="italic"><span href="#/xhtml/10_Chapter03#rglas29" id="glas29" title="Link target outside of Sample">Gefreiter</span></em></td>
<td class="tab2">Private 1st Class</td>
</tr>
<tr>
<td class="tab1"><em class="italic"><span href="#/xhtml/10_Chapter03#rglas30" id="glas30" title="Link target outside of Sample">Unteroffizier</span></em></td>
<td class="tab2">Corporal, no aircrew equivalent in Fighter Command.</td>
</tr>
<tr>
<td class="tab1"><em class="italic"><span href="#/xhtml/09_Chapter02#rglas31" id="glas31" title="Link target outside of Sample">Feldwebel</span></em></td>
<td class="tab2">Sergeant</td>
</tr>
<tr>
<td class="tab1"><em class="italic"><span href="#/xhtml/09_Chapter02#rglas32" id="glas32" title="Link target outside of Sample">Oberfeldwebel</span></em></td>
<td class="tab2">Flight Sergeant</td>
</tr>
<tr>
<td class="tab1"><em class="italic"><span href="#/xhtml/15_Chapter08#rglas33" id="glas33" title="Link target outside of Sample">Leutnant</span></em></td>
<td class="tab2">Pilot Officer</td>
</tr>
<tr>
<td class="tab1"><em class="italic"><a href="#/xhtml/08_Chapter01#rglas34" id="glas34">Oberleutnant</a></em></td>
<td class="tab2">Flight Lieutenant</td>
</tr>
<tr>
<td class="tab1"><em class="italic"><span href="#/xhtml/15_Chapter08#rglas35" id="glas35" title="Link target outside of Sample">Hauptmann</span></em></td>
<td class="tab2">Squadron Leader</td>
</tr>
<tr>
<td class="tab1"><em class="italic"><a href="#/xhtml/08_Chapter01#rglas36" id="glas36">Major</a></em></td>
<td class="tab2">Wing Commander</td>
</tr>
<tr>
<td class="tab1"><em class="italic"><span href="#/xhtml/11_Chapter04#rglas37" id="glas37" title="Link target outside of Sample">Oberst</span></em></td>
<td class="tab2">Group Captain</td>
</tr>
</tbody></table>
<p class="noindent">RAF Abbreviations: -</p>
<table class="table">
<tbody><tr>
<td class="td1">AAF</td>
<td class="td2">Auxiliary Air Force</td>
</tr>
<tr>
<td class="td1">AASF</td>
<td class="td2">Advance Air Striking Force</td>
</tr>
<tr>
<td class="td1">A&amp;AEE</td>
<td class="td2">Aeroplane &amp; Armament Experimental Establishment</td>
</tr>
<tr>
<td class="td1">AFC</td>
<td class="td2">Air Force Cross</td>
</tr>
<tr>
<td class="td1">AFDU</td>
<td class="td2">Air Fighting Development Unit</td>
</tr>
<tr>
<td class="td1">AI</td>
<td class="td2">Airborne Interception radar</td>
</tr>
<tr>
<td class="td1">AOC</td>
<td class="td2">Air Officer Commanding</td>
</tr>
<tr>
<td class="td1">AOC-in-C</td>
<td class="td2">Air Officer Commanding-in-Chief</td>
</tr>
<tr>
<td class="td1">ATA</td>
<td class="td2">Air Transport Auxiliary</td>
</tr>
<tr>
<td class="td1">ATS</td>
<td class="td2">Armament Training School</td>
</tr>
<tr>
<td class="td1">BEF</td>
<td class="td2">British Expeditionary Force</td>
</tr>
<tr>
<td class="td1">CAS</td>
<td class="td2">Chief of the Air Staff</td>
</tr>
<tr>
<td class="td1">CFS</td>
<td class="td2">Central Flying School</td>
</tr>
<tr>
<td class="td1">CGS</td>
<td class="td2">Central Gunnery School</td>
</tr>
<tr>
<td class="td1">CO</td>
<td class="td2">Commanding Officer</td>
</tr>
<tr>
<td class="td1">DES</td>
<td class="td2">Direct Entry Scheme</td>
</tr>
<tr>
<td class="td1">DFC</td>
<td class="td2">Distinguished Flying Cross</td>
</tr>
<tr>
<td class="td1">DFM</td>
<td class="td2">Distinguished Flying Medal</td>
</tr>
<tr>
<td class="td1">DSO</td>
<td class="td2">Distinguished Service Order</td>
</tr>
<tr>
<td class="td1">E/A</td>
<td class="td2">Enemy Aircraft</td>
</tr>
<tr>
<td class="td1">EFTS</td>
<td class="td2">Elementary Flying Training School</td>
</tr>
<tr>
<td class="td1">FAA</td>
<td class="td2">Fleet Air Arm</td>
</tr>
<tr>
<td class="td1">FIU</td>
<td class="td2">Fighter Interception Unit</td>
</tr>
<tr>
<td class="td1">FTS</td>
<td class="td2">Flying Training School</td>
</tr>
<tr>
<td class="td1">ITW</td>
<td class="td2">Initial Training Wing</td>
</tr>
<tr>
<td class="td1"><a id="page_xxii"></a>LAC</td>
<td class="td2">Leading Aircraftman</td>
</tr>
<tr>
<td class="td1">MRAF</td>
<td class="td2">Marshal of the Royal Air Force</td>
</tr>
<tr>
<td class="td1">MSFU</td>
<td class="td2">Merchant Ship Fighter Unit</td>
</tr>
<tr>
<td class="td1">NCO</td>
<td class="td2">Non-Commissioned Officer</td>
</tr>
<tr>
<td class="td1">ORB</td>
<td class="td2">Operations Record Book</td>
</tr>
<tr>
<td class="td1">OTC</td>
<td class="td2">Officer Training Corps</td>
</tr>
<tr>
<td class="td1">OTU</td>
<td class="td2">Operational Training Unit</td>
</tr>
<tr>
<td class="td1">PDC</td>
<td class="td2">Personnel Distribution Centre</td>
</tr>
<tr>
<td class="td1">RAFVR</td>
<td class="td2">Royal Air Force Volunteer Reserve</td>
</tr>
<tr>
<td class="td1">RFS</td>
<td class="td2">Reserve Flying School</td>
</tr>
<tr>
<td class="td1">RN</td>
<td class="td2">Royal Navy</td>
</tr>
<tr>
<td class="td1">RNAS</td>
<td class="td2">Royal Navy Air Service</td>
</tr>
<tr>
<td class="td1">SASO</td>
<td class="td2">Senior Air Staff Officer</td>
</tr>
<tr>
<td class="td1">SOO</td>
<td class="td2">Senior Operations Officer</td>
</tr>
<tr>
<td class="td1">SSC</td>
<td class="td2">Short Service Commission</td>
</tr>
<tr>
<td class="td1">UAS</td>
<td class="td2">University Air Squadron</td>
</tr>
<tr>
<td class="td1">U/S</td>
<td class="td2">Unserviceable</td>
</tr>
</tbody></table>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// 07_Authors.vue?097c0958